/* SHC-style cart for the Horizon/BuildMyPOD theme (2026-09-13). Two jobs:
   1. <shc-cart-recommendations>: lazy-loads the "Pairs great with" rail through the Section
      Rendering API and drops products already in the cart. Arrow buttons scroll the rail.
   2. "Added to your cart" state on the drawer: a cart:update event coming from a product form
      sets data-shc-cart-added on <html>; the CSS then swaps the heading and shows only the newest
      line. Opening from the cart icon, or closing the dialog, clears it. The flag lives on <html>
      because the header section (which contains the drawer) is morphed after every cart change. */
(function () {
  const html = document.documentElement;
  const isDrawerOpen = () => !!document.querySelector('cart-drawer-component dialog[open]');

  if (!customElements.get('shc-cart-recommendations')) {
    customElements.define(
      'shc-cart-recommendations',
      class extends HTMLElement {
        static get observedAttributes() { return ['data-product-id']; }
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-shc-rec-direction]');
            const rail = this.querySelector('.shc-recommendations__rail');
            if (!button || !rail) return;
            rail.scrollBy({
              left: Number(button.dataset.shcRecDirection) * rail.clientWidth * 0.8,
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
            });
          });
          const dialog = this.closest('dialog');
          if (dialog) {
            this.observer = new MutationObserver(() => { if (dialog.open) this.load(); });
            this.observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
            if (dialog.open) queueMicrotask(() => this.load());
          } else {
            this.observer = new IntersectionObserver((entries) => {
              if (entries.some((e) => e.isIntersecting)) { this.load(); this.observer.disconnect(); }
            }, { rootMargin: '200px' });
            this.observer.observe(this);
            /* The empty element has no height, so the observer can miss it: load once shortly after connect. */
            setTimeout(() => this.load(), 400);
          }
        }
        attributeChangedCallback(name, oldValue, newValue) {
          if (name === 'data-product-id' && oldValue && oldValue !== newValue && this.isConnected) {
            const dialog = this.closest('dialog');
            if (!dialog || dialog.open) this.load();
          }
        }
        disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); }
        async load() {
          const productId = this.dataset.productId;
          if (!productId || this.loadedId === productId || this.pendingId === productId) return;
          this.controller?.abort();
          this.controller = new AbortController();
          this.pendingId = productId;
          try {
            const url = new URL(this.dataset.url || '/recommendations/products', location.origin);
            url.searchParams.set('section_id', 'shc-cart-recommendations');
            url.searchParams.set('product_id', productId);
            url.searchParams.set('limit', '10');
            url.searchParams.set('intent', 'related');
            const response = await fetch(url, { signal: this.controller.signal });
            if (!response.ok) throw new Error('Recommendations unavailable');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (!this.isConnected || this.dataset.productId !== productId) return;
            const content = doc.querySelector('[data-shc-recommendations-content]');
            if (content) {
              if (this.dataset.headingId) {
                content.setAttribute('aria-labelledby', this.dataset.headingId);
                const h = content.querySelector('h3');
                if (h) h.id = this.dataset.headingId;
              }
              const excluded = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
              content.querySelectorAll('[data-shc-recommendation-id]').forEach((card) => {
                if (excluded.has(card.dataset.shcRecommendationId)) card.remove();
              });
              if (content.querySelector('[data-shc-recommendation-id]')) this.replaceChildren(content);
              else this.replaceChildren();
            }
            this.loadedId = productId;
          } catch (error) {
            if (error.name !== 'AbortError') this.loadedId = productId;
          } finally {
            if (this.pendingId === productId) this.pendingId = null;
          }
        }
      }
    );
  }

  /* "Added" flag: product forms dispatch cart:update with data.source = 'product-form-component'. */
  document.addEventListener('cart:update', (event) => {
    const source = event?.detail?.data?.source || '';
    if (/product-form/i.test(source) || event?.detail?.data?.variantId) {
      html.setAttribute('data-shc-cart-added', '');
    }
  });
  /* Opening from the header icon shows the full cart. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('.cart-drawrer-btn, [on\\:click="/open"]')) html.removeAttribute('data-shc-cart-added');
  }, true);
  /* Closing the dialog resets the state for next time. */
  const watchDialogs = () => {
    document.querySelectorAll('cart-drawer-component dialog').forEach((dialog) => {
      if (dialog.__shcWatched) return;
      dialog.__shcWatched = true;
      const reset = () => setTimeout(() => { if (!isDrawerOpen()) html.removeAttribute('data-shc-cart-added'); }, 250);
      dialog.addEventListener('close', reset);
      new MutationObserver(() => { if (!dialog.open) reset(); }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  };
  watchDialogs();
  new MutationObserver(watchDialogs).observe(document.body, { childList: true, subtree: true });

  /* Cart page: the sticky Subtotal/Checkout bar is server-rendered inside the cart section, but a Horizon
     ancestor (container-type) turns its position:fixed into a 1500px box that widens the page. Move it to
     <body>, and move the fresh copy again after every section morph (quantity changes re-render it). */
  const moveSticky = () => {
    const fresh = document.querySelector('main .shc-cart-page__sticky');
    if (fresh) {
      document.querySelectorAll('body > .shc-cart-page__sticky').forEach((e) => e.remove());
      document.body.appendChild(fresh);
    } else if (document.querySelector('.cart-page--empty')) {
      document.querySelectorAll('body > .shc-cart-page__sticky').forEach((e) => e.remove());
    }
  };
  moveSticky();
  new MutationObserver(moveSticky).observe(document.body, { childList: true, subtree: true });
})();
